import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

interface Props {
  src: string;
  muted?: boolean;
  controls?: boolean;
  className?: string;
}

/**
 * HLS player. Uses hls.js where MSE is supported, falls back to native HLS
 * (Safari, iOS) otherwise.
 *
 * Audio is muted by default — this is enforced at the schema level for demo
 * cameras anyway, but defense in depth.
 */
export default function HlsPlayer({ src, muted = true, controls = false, className }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: true,
        liveSyncDuration: 4,
        liveMaxLatencyDuration: 10,
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari / iOS native HLS
      video.src = src;
    } else {
      console.error('HLS not supported in this browser');
    }

    return () => {
      hls?.destroy();
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      muted={muted}
      autoPlay
      playsInline
      controls={controls}
      className={className}
      style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
    />
  );
}
